import { ArrowRight, AudioLines, Camera, DollarSign, FileText, Languages, Mic, PieChart, Ruler, Search, ShieldCheck, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { InitialsAvatar, VoiceBubble } from "./landingPrimitives";

/* ============================================================
 * §6 · TALK TO LEVI · SHOW HIM THE JOB — cool outdoor environment
 * ============================================================ */
export const TalkVisionSection = ({ c }) => {
  const capabilities = [
    { icon: AudioLines, title: c.talk.c1_title, body: c.talk.c1_body, testid: "talk-card-voice" },
    { icon: Mic,        title: c.talk.c2_title, body: c.talk.c2_body, testid: "talk-card-handsfree" },
    { icon: Languages,  title: c.talk.c3_title, body: c.talk.c3_body, testid: "talk-card-bilingual" },
    { icon: Camera,     title: c.talk.c4_title, body: c.talk.c4_body, testid: "talk-card-vision" },
    { icon: Ruler,      title: c.talk.c5_title, body: c.talk.c5_body, testid: "talk-card-verify" },
    { icon: Search,     title: c.talk.c6_title, body: c.talk.c6_body, testid: "talk-card-gaps" },
  ];
  const chipIcons = [AudioLines, Mic, Languages, Camera, Ruler, Search];
  const RightUI = () => (
    <div className="space-y-3" data-testid="talk-right-stack">
      <VoiceBubble testid="talk-contractor-voice">{c.talk.contractor_voice}</VoiceBubble>
      <div className="rounded-2xl bg-white shadow-lg border border-white p-4 flex items-start gap-3" data-testid="talk-levi-verify">
        <InitialsAvatar initials="L" size={32} variant="navy" />
        <div className="text-[14px] lg:text-[13px] text-[var(--jc-text)] leading-snug">{c.talk.levi_verify}</div>
      </div>
      <div className="rounded-2xl bg-white shadow-lg border border-white p-4 flex items-start gap-3" data-testid="talk-levi-observation">
        <InitialsAvatar initials="L" size={32} variant="navy" />
        <div className="text-[14px] lg:text-[13px] text-[var(--jc-text)] leading-snug">{c.talk.levi_observation}</div>
      </div>
      <div className="rounded-2xl bg-white shadow-xl border border-white p-4" data-testid="talk-opening">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[var(--jc-blue)] grid place-items-center text-white"><Search size={18} /></div>
          <div className="flex-1">
            <div className="text-[13px] lg:text-[12.5px] uppercase tracking-wider font-semibold text-[var(--jc-text-soft)]">{c.talk.opening_title}</div>
            <div className="text-[15.5px] lg:text-[14.5px] font-bold text-[var(--jc-text)] leading-tight">{c.talk.opening_kind}</div>
          </div>
        </div>
        <div className="mt-2.5 rounded-lg border-2 border-dashed border-[var(--jc-blue)]/60 bg-[#EEF3FE]/60 px-3 py-3 text-center">
          <div className="text-[16px] lg:text-[15px] font-bold text-[var(--jc-blue)]">{c.talk.opening_size}</div>
        </div>
      </div>
    </div>
  );
  return (
    <section
      data-testid="features-talk"
      className="relative lg:min-h-[1420px] isolate overflow-hidden"
      style={{ background: "#E7EEF9" }}
    >
      <div className="hidden lg:block absolute inset-x-0 top-0 h-[960px] pointer-events-none" style={{ zIndex: 0 }} aria-hidden>
        <img
          src="/brand/features/talk_env_canvas.png"
          alt="A contractor in painter's whites kneeling and holding a phone to scan a wall while Levi sits beside him. Paint tray with roller on the floor. Estimating walkthrough scene."
          draggable={false}
          data-testid="talk-env-canvas"
          className="w-full h-full object-cover object-center select-none"
        />
      </div>

      <div className="relative w-full px-6 lg:pl-[5%] lg:pr-6 pt-16 pb-12 lg:pt-20 lg:pb-16" style={{ zIndex: 2 }}>
        <div className="grid grid-cols-12 gap-6 lg:gap-8 items-start">
          <div className="col-span-12 lg:col-span-6" data-testid="talk-left-zone">
            <h2 className="font-display tracking-tight text-[#0A1428] text-4xl sm:text-5xl lg:text-[54px] leading-[1.02]" data-testid="talk-headline">
              {c.talk.h2a}<br />{c.talk.h2b_pre} <span className="text-[var(--jc-blue)]">{c.talk.h2b_blue}</span>
            </h2>
            <p className="mt-4 text-[16.5px] lg:text-[17px] leading-[1.55] text-[var(--jc-text-soft)] max-w-lg" data-testid="talk-body">{c.talk.lede}</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-[540px]" data-testid="talk-cards">
              {capabilities.map((cc) => (
                <div key={cc.testid} data-testid={cc.testid} className="rounded-2xl bg-white/95 border border-white shadow-md p-4 flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[var(--jc-blue)] grid place-items-center text-white">
                    <cc.icon size={20} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-bold leading-tight text-[var(--jc-text)]">{cc.title}</div>
                    <div className="text-[12.5px] text-[var(--jc-text-soft)] leading-snug mt-1">{cc.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/schedule-a-demo" className="mt-8 jc-btn-blue text-white px-6 py-3.5 rounded-md text-[15px] font-semibold inline-flex items-center gap-2" data-testid="talk-cta">
              {c.talk.cta} <ArrowRight size={16} />
            </Link>
          </div>

          <div className="hidden lg:block lg:col-span-6" data-testid="talk-right-zone">
            <div className="ml-auto w-[300px]"><RightUI /></div>
          </div>
        </div>

        {/* Dark capability bar */}
        <div className="hidden lg:flex justify-center mt-12 lg:mt-[140px]" data-testid="talk-chip-bar">
          <div className="rounded-full bg-[#0A1128] border border-white/10 px-6 py-3 flex items-center gap-8 shadow-2xl">
            {chipIcons.map((Icon, idx) => (
              <span key={idx} className="flex items-center gap-2 text-white">
                <Icon size={18} className="text-[var(--jc-blue)]" />
                <span className="text-[13px] font-semibold whitespace-nowrap">{c.talk.chip_bar[idx]}</span>
              </span>
            ))}
          </div>
        </div>

        {/* White five-benefit closing strip (matches Section 5) */}
        <div className="hidden lg:block max-w-none mx-auto px-[2%] mt-8 lg:mt-10">
          <div
            className="rounded-2xl bg-white/95 border border-white shadow-xl px-8 lg:px-10 py-7 lg:py-8"
            data-testid="talk-value-strip"
          >
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
              {c.handle.value.map((v, i) => {
                const Icon = [DollarSign, PieChart, FileText, ShieldCheck, TrendingUp][i];
                return (
                  <div key={i} data-testid={`talk-value-${i+1}`} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[#EEF3FE] grid place-items-center text-[var(--jc-blue)]">
                      <Icon size={28} strokeWidth={2.2} />
                    </div>
                    <div>
                      <div className="text-[17px] font-bold text-[var(--jc-text)] leading-tight">{v.t}</div>
                      <div className="text-[14.5px] text-[var(--jc-text-soft)] leading-snug mt-1.5">{v.b}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile stack */}
      <div className="lg:hidden max-w-7xl mx-auto px-6 pb-8 space-y-6" data-testid="talk-mobile-env">
        <div className="w-full h-[420px] rounded-2xl overflow-hidden shadow-xl bg-[#0B1E3E]">
          <img src="/brand/features/talk_env_canvas.png" alt="Painter estimating a wall with phone while Levi sits beside him." draggable={false} className="w-full h-full object-cover select-none" style={{ objectPosition: "60% 55%", transform: "scale(1.25)", transformOrigin: "60% 55%" }} />
        </div>
        <RightUI />
        <div className="rounded-2xl bg-[#0A1128] border border-white/10 px-4 py-4 shadow-lg" data-testid="talk-chip-grid-mobile">
          <div className="grid grid-cols-2 gap-x-3 gap-y-3.5">
            {chipIcons.map((Icon, idx) => (
              <span key={idx} className="flex items-center gap-2 text-white min-w-0">
                <Icon size={16} className="text-[var(--jc-blue)] flex-shrink-0" />
                <span className="text-[12.5px] font-semibold leading-tight">{c.talk.chip_bar[idx]}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
